import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { URLS } from 'src/app/core/constants/ConstantVariables';
import { Environment } from 'src/app/environment/environment';

/** EN-24306 — one entry of the client switcher (an employee row of the signed-in person). */
export interface PortalClient {
  employeeId: number;
  employeeGuid: string;
  clientId: number;
  clientName: string | null;
  mainClientId: number;
  mainClientName: string | null;
  hireDate: string | null;
  onboardingPending: boolean;
  isTerminated: boolean;
  // Last day a terminated row can be opened (its client's access window); after it the row is no longer listed.
  accessEndsOn: string | null;
  isDefault: boolean;
  isCurrent: boolean;
}

// Read by the header interceptor too, which must not inject services that depend on AuthService.
export const SELECTED_EMPLOYEE_ID_KEY = 'selectedEmployeeId';

/** EN-24306 — the stored client selection; the interceptor sends it as switchEmployeeId on every refresh. */
export function getStoredEmployeeSelection(): number | null {
  const value = Number(localStorage.getItem(SELECTED_EMPLOYEE_ID_KEY));
  return Number.isFinite(value) && value > 0 ? value : null;
}

/**
 * EN-24306 — a PEO employee has one employee row per client but a single portal login. This holds the rows
 * the login can switch between; AuthService does the switching (SSO re-issues the token for the chosen row).
 * Kept free of AuthService so AuthService can depend on it.
 */
@Injectable({
  providedIn: 'root',
})
export class CurrentClientService {
  private http = inject(HttpClient);
  private environment = new Environment();

  readonly clients$ = new BehaviorSubject<PortalClient[]>([]);
  // The SSO user the list was loaded for; a different login in the same tab loads its own list.
  private loadedForUserId: string | null = null;

  isLoadedFor(userId: string | undefined): boolean {
    return !!userId && this.loadedForUserId === userId;
  }

  /**
   * Null when the list could not be read. The list is then not marked loaded (the next page load tries again)
   * and the caller leaves the stored selection alone: treating a failed call as "no clients" would drop the
   * selection and let the next token refresh move the session back to the login's own row.
   */
  async load(userId: string): Promise<PortalClient[] | null> {
    let clients: PortalClient[];
    try {
      const response = await firstValueFrom(
        this.http.get<any>(
          this.environment.getApiConnection() +
            'api/v1/' +
            URLS.Controller.User.Name +
            URLS.Controller.User.GetMyClients
        )
      );
      clients = response?.data ?? [];
    } catch {
      return null;
    }
    this.loadedForUserId = userId;
    this.clients$.next(clients);
    return clients;
  }

  markCurrent(employeeId: number): void {
    this.clients$.next(
      this.clients$.value.map((c) => ({ ...c, isCurrent: c.employeeId === employeeId }))
    );
  }

  setStoredSelection(employeeId: number | null): void {
    if (employeeId) {
      localStorage.setItem(SELECTED_EMPLOYEE_ID_KEY, employeeId.toString());
    } else {
      localStorage.removeItem(SELECTED_EMPLOYEE_ID_KEY);
    }
  }

  reset(): void {
    this.loadedForUserId = null;
    this.clients$.next([]);
  }
}
